import React, { useCallback, useEffect, useMemo } from "react";
import { BreadcrumbItem, Nav, NavItem, NavbarText } from "reactstrap";
import { useHistory } from "react-router-dom";
import { useQuery } from "@apollo/client";
import _get from "lodash/get";
import * as Notify from "@repoflow/form-schema/notify";
import * as Section from "@repoflow/components/section";
import * as Preview from "@repoflow/components/preview";
import { Provider as EditorProvider } from "@repoflow/components/editor";
import { Layout } from "@repoflow/components/layout-dual";
import { Content as ContentAvailable } from "@repoflow/components/content-available";
import { useNavbarContext } from "@repoflow/components/navbar";
import { useSidebarContext } from "@repoflow/components/sidebar-menu";
import ContentTabs, { useContentState } from "@repoflow/components/content-tabs";
import * as PageQL from "queries/page";
import * as SectionQL from "queries/section";
import * as SiteQL from "queries/site";
import * as BlogCollectionQL from "queries/blog-collection";

import { GROUP_SCAN } from "../translations";
import { liveUrlOf, useLiveHost } from "../../live";
import { servedRouteOf } from "@common/schema/page/published";
import { Provider as EditorPageProvider, useEditorPage } from "./context";
import { otherLanguages } from "./translation";
import * as ElementsTab from "./elements";
import * as PageTab from "./page";
import * as ParamsTab from "./params";
import { Frame } from "./frame";

export const Sidebar = () => null;

export const Indice = () => (
  <>
    <BreadcrumbItem>Edit</BreadcrumbItem>
  </>
);

// The page editor: what the page is made of on the left, what it looks like on
// the right, and one Save for both.
//
// Ported from the archived project's site-frontend page editor
// (`routes/domain/site/pages/page/edit`). The shape is deliberately the same —
// one mutation over the whole page, three tabs that are views onto its single
// form state, a nested route per element, and a live preview in the other pane
// — because that shape is what makes a page editable at all: every tab writes
// into one value object, so Save is one decision and the preview has one thing
// to watch.
//
// The Page tab carries gen-2's `pageConfig` too — the document shell, title,
// meta, favicon, body template and the injected scripts and styles, merged over
// the site's. It waited until a served page existed to read it: while a preview
// was the only reader, a stored field, a merge and a renderer branch bought
// nothing a site-wide shell did not already give.
//
// What is NOT ported: seven of gen-2's nine element types. See
// common/schema/page-element/fields.
export const Content = ({
  match: {
    params,
    params: { siteId, pageId },
  },
}) => {
  const {
    base: parentBase,
    basepath: parentBasepath,
    schema,
    // The page as it is STORED, not as the form holds it. What the Elements tab
    // says about the group has to be a statement about the rows that exist —
    // an unsaved edit to `translationKey` names a group nothing is in yet.
    values: stored,
  } = Section.useSectionContext();

  const base = `${parentBase}/edit`;
  const basepath = `${parentBasepath}/edit`;

  // cache-first, NOT cache-and-network. The latter answers twice — cache, then
  // network — and the second answer is a new `data` identity, which remakes
  // `sections`, then the form config, then the widget tree beneath the OPEN
  // element modal, detaching the control being typed into (P139).
  //
  // Freshness is not lost: creating a section evicts every cache key containing
  // `sections/queries`, so the next read here misses the cache anyway.
  //
  // A failure is deliberately not surfaced. The picker falls back to whatever
  // each element already points at, so a page whose sections could not be
  // listed still edits and still saves.
  const { data } = useQuery(SectionQL.list, {
    variables: { siteId, input: { limit: 100 } },
    fetchPolicy: "cache-first",
  });

  // `|| []` mints a new array on every render while the query is in flight, and
  // that identity is what everything downstream is memoised on.
  const sections = useMemo(
    () => _get(data, "viewer.account.sites.get.sections.list.items") || [],
    [data]
  );

  // The languages this site declares, for the Page tab's locale picker.
  //
  // Everything above applies again, unchanged: cache-first because a second
  // answer is a second `data` identity and every config downstream is memoised
  // on it (P139), and a failure is not surfaced because the picker degrades to
  // the plain text field a page's locale was before it (see ./page).
  //
  // This is a cache READ rather than a round trip on all but the coldest entry
  // — the site's own screens above this one resolve the same query, and the
  // Translate screen already reads it here for the same list.
  // The site's collections, for the blog-post-collection element's select —
  // the same read, the same policy and the same memo as the sections above.
  const { data: collectionsData } = useQuery(BlogCollectionQL.list, {
    variables: { siteId, input: { limit: 100 } },
    fetchPolicy: "cache-first",
  });

  const collections = useMemo(
    () => _get(collectionsData, "viewer.account.sites.get.blog.collections.list.items") || [],
    [collectionsData]
  );

  const { data: siteData } = useQuery(SiteQL.get, {
    variables: { siteId },
    fetchPolicy: "cache-first",
  });

  const locales = useMemo(
    () => _get(siteData, "viewer.account.sites.get.locales") || [],
    [siteData]
  );

  // The site's pages, and only when this one is in a group — a page nobody has
  // translated asks the server nothing. Same variables the view and Translate
  // screens send, so all three share one cache entry and walking here from
  // either of them is free.
  //
  // `cache-first` for the reason the two queries above give: a second answer is
  // a second `data` identity and this component re-renders on it, which is the
  // event every memo on this screen exists to absorb (P139). The memo below is
  // what stops `languages` being a new array each time — the tab reads it, and
  // an unstable one would re-render the note on every keystroke elsewhere.
  //
  // No error handling either, for a smaller reason: the only thing that
  // degrades is a sentence about the other languages.
  const { data: pagesData } = useQuery(PageQL.list, {
    variables: { siteId, input: { limit: GROUP_SCAN } },
    fetchPolicy: "cache-first",
    skip: !(stored && stored.translationKey),
  });

  // The other languages this page exists in, as codes. The unwrap is INSIDE the
  // memo for the reason the view screen's is: a `||` outside it is a fresh
  // array every render, which is a dependency that always differs.
  const languages = useMemo(
    () =>
      otherLanguages(
        _get(siteData, "viewer.account.sites.get") || null,
        stored,
        _get(pagesData, "viewer.account.sites.get.pages.list.items") || []
      ),
    [siteData, pagesData, stored]
  );

  // Which tab is open. This lives HERE, above the mutation, and reaches the
  // tree through the context below rather than through props.
  //
  // That is not a style choice. `Section.Mutation` renders `<Children/>` from
  // whatever it is handed, so passing an INLINE arrow — the obvious way to add
  // a prop — makes React see a new component type on every render of this
  // function and remount everything under it. This component re-renders
  // whenever Apollo touches the cache, so the effect is an editor that
  // silently throws away the element you have open, several seconds after you
  // opened it. Same mechanism as P144, self-inflicted; `{MutationHandler}` is
  // a stable reference and is what keeps it from happening.
  const tabs = useContentState({ selected: 0 });
  const history = useHistory();

  // Which element is open is the URL, not state — see ./context. These two are
  // the only places that write it.
  //
  // `tabs` is deliberately not a dependency: it is a fresh object every render
  // and would make `openElement` a fresh identity every render, which is what
  // the element list's `config` is memoised on (P139). Its `select` closes over
  // nothing but a `useState` setter, so the first render's copy stays correct.
  const openElement = useCallback(
    (index) => {
      // The element screen renders INSIDE the Elements tab, so opening one from
      // the Page tab has to bring that tab forward too — otherwise the
      // preview's double-click appears to do nothing.
      tabs.select(0);
      history.push(`${base}/element/${index}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, history]
  );

  // Guarded rather than an unconditional replace: this is every tab's onClick,
  // and a replace onto the URL we are already on is still a location change —
  // which re-renders every descendant of the Router for nothing.
  const closeElement = useCallback(() => {
    if (history.location.pathname.startsWith(`${base}/element`)) {
      history.replace(base);
    }
  }, [base, history]);

  // `tabs` is a fresh object each render, so this memo only really holds while
  // nothing above re-renders — which is the common case, and it is enough to
  // keep the section list's identity stable for the form configs downstream.
  // `storedRoute` is the route as it is SAVED, and it is on the context for the
  // reason the three lists above are: the navbar reads it, the navbar lives
  // under the mutation, and the mutation's own value object holds the route the
  // form is editing rather than the one the service is serving. See the link in
  // MutationHandler for why the difference matters there.
  //
  // Unwrapped to a STRING before the memo depends on it, never `stored` itself.
  // The row is a new object identity every time the route's query re-answers,
  // and this memo is what holds the element list's `config` still (P139) — a
  // dependency on the object would rebuild the widget tree for a value that did
  // not change.
  // The SERVED route, since publishing: a saved route the author has not
  // published yet is not where the page is, and a page nothing has published
  // is nowhere. Same rule as LiveLink — see @common/schema/page/published.
  const storedRoute = servedRouteOf(stored);

  const editorPage = useMemo(
    () => ({
      siteId,
      pageId,
      sections,
      collections,
      locales,
      languages,
      storedRoute,
      tabs,
      openElement,
      closeElement,
    }),
    [siteId, pageId, sections, collections, locales, languages, storedRoute, tabs, openElement, closeElement]
  );

  return (
    <EditorProvider>
      <Preview.Provider>
        <EditorPageProvider value={editorPage}>
          <Section.Provider
            schema={schema}
            base={base}
            basepath={basepath}
            attributes={params}
          >
            <Section.Mutation
              target={{ $ref: "/Public/Page/Update" }}
              mutation={PageQL.edit}
              update={PageQL.update({ pageId })}
              // An element is a route beneath this one and this form outlives
              // it — see openElement/closeElement — so a move inside `base`
              // is not leaving the page and must not ask about unsaved edits.
              within={base}
              // The editor does NOT navigate away on save, so nothing remounts
              // the route's query — see the same refetch on the section editor
              // for what that costs. Here it matters more: you save several
              // times in one sitting.
              refetch={[{ query: PageQL.get, variables: { siteId, pageId } }]}
              onMutate={({ handler, state: { values } }) => {
                handler({ variables: { siteId, pageId, input: values } });
              }}
              onCompleted={() => {}}
              onError={(error) => {
                Notify.error(error.toString());
              }}
            >
              {MutationHandler}
            </Section.Mutation>
          </Section.Provider>
        </EditorPageProvider>
      </Preview.Provider>
    </EditorProvider>
  );
};

const MutationHandler = ({ state, attributes: { siteId } }) => {
  const navbar = useNavbarContext();
  const { setOpen } = useSidebarContext();
  const { tabs, closeElement, storedRoute } = useEditorPage();
  const page = state.values;

  // The hostname this page is published on, "" until a domain is verified. Read
  // here rather than passed down from Content: this is the only part of the
  // editor that wants it, and the query is `cache-first` (see ../../live).
  const host = useLiveHost(siteId);

  // The editor wants the width. Collapsed on entry, restored on the way out —
  // the sidebar is a global, so leaving it collapsed would follow you to every
  // other screen.
  useEffect(() => {
    setOpen(false);
    return () => setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Which page, and where it would live. The breadcrumb says the title; this
  // says the route, which is the thing you are editing against and the thing
  // the preview's `{{page.route}}` resolves to.
  //
  // And the route is the LINK OUT, once the site has a verified domain: this
  // line already names the address, and the editor is where an author most
  // wants to hold what they are building against what is currently served. Not
  // a fourth button beside Save — everything in that strip changes the page,
  // and this leaves it.
  //
  // TWO STATES DRAW PLAIN TEXT, which is what this line has always been:
  //
  //   - no verified domain, so there is nowhere to go (see ../../live);
  //   - an UNSAVED route. The form's value is what the line shows, because that
  //     is the route being edited, but nothing answers on it yet — so linking
  //     it would 404, and linking the stored one instead would be a link whose
  //     text and destination are two different pages. Save, and it is a link.
  const liveHref = liveUrlOf(host, page.route, storedRoute);

  useEffect(() => {
    const Item = () => (
      <NavbarText className="ml-2">
        Page/<b>{page.title}</b>
        {page.route ? (
          <>
            {" "}
            on{" "}
            {liveHref ? (
              <a
                href={liveHref}
                target="_blank"
                rel="noopener noreferrer"
                title={liveHref}
              >
                {page.route}{" "}
                <i className="fa fa-external-link" aria-hidden="true" />
              </a>
            ) : (
              page.route
            )}
          </>
        ) : null}
      </NavbarText>
    );

    navbar.push(Item);
    return () => navbar.pop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.title, page.route, liveHref]);

  const items = [
    {
      label: "Elements",
      content: <ElementsTab.Content />,
      // Every tab closes whatever element is open, including this one: clicking
      // "Elements" while an element is on screen means "show me the elements".
      // Without it the tab is a no-op from the only place it is worth pressing.
      onClick: closeElement,
    },
    {
      label: "Page",
      content: <PageTab.Content />,
      // Coming back to a tab still showing element 3, with nothing on screen
      // saying so, is worse than coming back to the list.
      onClick: closeElement,
    },
    {
      label: "Editor",
      content: <ParamsTab.Content />,
      onClick: closeElement,
    },
  ];

  return (
    <Layout
        left={
          <>
            <div className="float-right">
              <Nav tabs>
                <NavItem>
                  {/* In the tab strip rather than under the form, because there
                      is no single form: three tabs and the element screen all
                      write the same value object, and a Save at the bottom of
                      one of them would read as saving only that one. */}
                  <Section.Button
                    enabledClass="nav-link text-success"
                    disabledClass="nav-link text-muted"
                  >
                    Save
                  </Section.Button>
                </NavItem>
              </Nav>
            </div>

            <ContentTabs control={tabs} tabs={items} frame={TabFrame} />
          </>
        }
      right={<Frame siteId={siteId} page={page} state={state} />}
    />
  );
};

// Each tab scrolls inside the pane rather than growing it. Without this the
// left pane grows with the element list and the preview drifts off screen.
const TabFrame = ({ children }) => (
  <ContentAvailable className="container-fluid px-2">{children}</ContentAvailable>
);
